import React from 'react';
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

/**
 * Lightweight markdown renderer for TenantHub release notes (no extra dependency).
 * Supports: headings, paragraphs, lists, tables, fenced code, inline code, bold, hr.
 */
const renderInline = (text) => {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) != null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <Box
          key={`c-${key++}`}
          component="code"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            bgcolor: 'rgba(15,23,42,0.06)',
            px: 0.6,
            py: 0.15,
            borderRadius: 0.75,
            fontSize: '0.85em'
          }}
        >
          {token.slice(1, -1)}
        </Box>
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const SimpleMarkdown = ({ content }) => {
  const lines = String(content || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (line.trim() === '---' || line.trim() === '***') {
      blocks.push(<Divider key={`hr-${i}`} sx={{ my: 1.5 }} />);
      i += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const code = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        code.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      blocks.push(
        <Box
          key={`code-${i}`}
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'rgba(15,23,42,0.92)',
            color: '#e2e8f0',
            overflowX: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '0.8rem',
            lineHeight: 1.5
          }}
        >
          {lang ? <Box sx={{ color: '#93c5fd', mb: 1, fontSize: '0.7rem' }}>{lang}</Box> : null}
          {code.join('\n')}
        </Box>
      );
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const variant = level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1';
      blocks.push(
        <Typography key={`h-${i}`} variant={variant} sx={{ fontWeight: 800, mt: level === 1 ? 0.5 : 1.5, mb: 0.75 }}>
          {renderInline(heading[2])}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1])) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const parseRow = (row) => row
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim());
      const header = parseRow(tableLines[0]);
      const bodyRows = tableLines.slice(2).map(parseRow);
      blocks.push(
        <Box key={`table-${i}`} sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {header.map((cell) => (
                  <TableCell key={cell} sx={{ fontWeight: 800 }}>{renderInline(cell)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bodyRows.map((row, idx) => (
                <TableRow key={`r-${idx}`}>
                  {row.map((cell, cidx) => (
                    <TableCell key={`${idx}-${cidx}`}>{renderInline(cell)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <Box key={`ul-${i}`} component="ul" sx={{ pl: 2.5, my: 0.5 }}>
          {items.map((item, idx) => (
            <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>
              {renderInline(item)}
            </Typography>
          ))}
        </Box>
      );
      continue;
    }

    const para = [line];
    i += 1;
    while (
      i < lines.length
      && lines[i].trim()
      && !lines[i].startsWith('#')
      && !lines[i].startsWith('```')
      && !lines[i].trim().startsWith('|')
      && !/^\s*[-*]\s+/.test(lines[i])
      && lines[i].trim() !== '---'
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <Typography key={`p-${i}`} variant="body2" sx={{ mb: 0.75, whiteSpace: 'pre-wrap' }}>
        {renderInline(para.join(' '))}
      </Typography>
    );
  }

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{blocks}</Box>;
};

export default SimpleMarkdown;
